import React, {useState} from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import {useTranslation} from 'react-i18next';
import {CommonActions} from '@react-navigation/native';
import {useAuth} from '../context/AuthContext';
import {navigationRef} from '../navigation/navigationRef';
import {IS_WEB} from '../utils/platform';
import {UIPressable} from '../components/ui-components/UIPressable';
import {UIScreenScrollWrapper} from '../components/ui-components/UIScreenScrollWrapper';
import {useTheme} from '../theme/ThemeContext';

const AuthScreen = ({navigation}) => {
  const {t} = useTranslation();
  const {theme} = useTheme();
  const {
    user,
    loading,
    signIn,
    signUp,
    signOut,
    syncNow,
    resetPasswordForEmail,
    updatePassword,
    passwordRecovery,
    isConfigured,
  } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const hasMissingSignInFields = !email.trim() || !password.trim();
  const isSignInDisabled = busy || hasMissingSignInFields;

  const styles = getStyles(theme);
  const scrollContentWeb =
    IS_WEB ? {minWidth: '100%', alignItems: 'center'} : undefined;

  const onSignIn = async () => {
    if (isSignInDisabled) {
      return;
    }
    setError('');
    setMessage('');
    setBusy(true);
    const {error: err} = await signIn(email, password);
    setBusy(false);
    if (err) {
      setError(
        err.code === 'AUTH_REQUEST_TIMEOUT' ? t('AUTH_REQUEST_TIMEOUT') : err.message,
      );
      return;
    }
    setMessage(t('AUTH_SIGNED_IN'));
    if (navigation.canGoBack?.()) {
      navigation.goBack();
    }
  };

  const onSignUp = async () => {
    setError('');
    setMessage('');
    setBusy(true);
    const {error: err, needsEmailConfirmation} = await signUp(email, password);
    setBusy(false);
    if (err) {
      setError(
        err.code === 'AUTH_REQUEST_TIMEOUT' ? t('AUTH_REQUEST_TIMEOUT') : err.message,
      );
      return;
    }
    if (needsEmailConfirmation) {
      setMessage(t('AUTH_CHECK_EMAIL'));
    } else {
      setMessage(t('AUTH_ACCOUNT_CREATED'));
      if (navigation.canGoBack?.()) {
        navigation.goBack();
      }
    }
  };

  const onSignOut = async () => {
    setError('');
    setMessage('');
    setBusy(true);
    try {
      await signOut();
      setMessage(t('AUTH_SIGNED_OUT'));
    } catch (e) {
      setError(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  const onSync = async () => {
    setBusy(true);
    await syncNow();
    setBusy(false);
    setMessage(t('AUTH_SYNC_DONE'));
  };

  const onSendResetEmail = async () => {
    setError('');
    setMessage('');
    setBusy(true);
    try {
      const {error: err} = await resetPasswordForEmail(email);
      if (err) {
        setError(
          err.code === 'AUTH_REQUEST_TIMEOUT'
            ? t('AUTH_REQUEST_TIMEOUT')
            : err.message,
        );
        return;
      }
      setMessage(t('AUTH_RESET_EMAIL_SENT'));
    } finally {
      setBusy(false);
    }
  };

  const goToMainApp = () => {
    const resetAction = CommonActions.reset({
      index: 0,
      routes: [{name: 'Tabs'}],
    });
    try {
      if (navigationRef.isReady?.()) {
        navigationRef.dispatch(resetAction);
      } else {
        navigation.dispatch(resetAction);
      }
    } catch {
      try {
        navigation.navigate('Tabs');
      } catch {
        // ignore
      }
    }
    if (IS_WEB && typeof globalThis.history?.replaceState === 'function') {
      try {
        globalThis.history.replaceState(
          {},
          '',
          `${globalThis.location.origin}/`,
        );
      } catch {
        // ignore
      }
    }
  };

  const onSaveNewPassword = async () => {
    setError('');
    setMessage('');
    if (newPassword !== confirmNewPassword) {
      setError(t('AUTH_PASSWORDS_DONT_MATCH'));
      return;
    }
    if (!newPassword?.length) {
      setError(t('AUTH_PASSWORD_REQUIRED'));
      return;
    }
    setBusy(true);
    try {
      const {error: err} = await updatePassword(newPassword);
      if (err) {
        setError(
          err.code === 'AUTH_REQUEST_TIMEOUT'
            ? t('AUTH_REQUEST_TIMEOUT')
            : err.message ?? String(err),
        );
        return;
      }
      setNewPassword('');
      setConfirmNewPassword('');
      setMessage(t('AUTH_PASSWORD_UPDATED'));
      goToMainApp();
    } catch (e) {
      setError(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  if (!isConfigured) {
    return (
      <UIScreenScrollWrapper contentContainerStyle={scrollContentWeb}>
        <View style={styles.box}>
          <Text style={styles.text}>{t('AUTH_NOT_CONFIGURED')}</Text>
        </View>
      </UIScreenScrollWrapper>
    );
  }

  if (loading && !user) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator color={theme.colors.primary} size="large" />
      </View>
    );
  }

  return (
    <UIScreenScrollWrapper contentContainerStyle={scrollContentWeb}>
      <View style={styles.box}>
        {user && passwordRecovery ? (
          <>
            <Text style={styles.text}>{t('AUTH_SET_NEW_PASSWORD_HINT')}</Text>
            <View style={styles.inputGroup}>
              <Text style={styles.label}>{t('AUTH_NEW_PASSWORD')}</Text>
              <TextInput
                style={styles.input}
                value={newPassword}
                onChangeText={setNewPassword}
                secureTextEntry
                placeholder="••••••••"
                placeholderTextColor={theme.colors.tabIcon}
              />
              <Text style={styles.label}>{t('AUTH_CONFIRM_PASSWORD')}</Text>
              <TextInput
                style={styles.input}
                value={confirmNewPassword}
                onChangeText={setConfirmNewPassword}
                secureTextEntry
                placeholder="••••••••"
                placeholderTextColor={theme.colors.tabIcon}
              />
            </View>
            <View style={styles.buttonGroup}>
              <UIPressable
                style={[styles.button, styles.buttonFullWidth]}
                onPress={onSaveNewPassword}
                disabled={busy}>
                <Text style={styles.buttonText}>{t('AUTH_SAVE_PASSWORD')}</Text>
              </UIPressable>
            </View>
          </>
        ) : user ? (
          <>
            <View style={styles.inputGroup}>
              <Text style={[styles.label, styles.labelGroupFirst]}>
                {t('AUTH_EMAIL')}
              </Text>
              <Text style={styles.text}>{user.email}</Text>
            </View>
            {/*
            <UIPressable
              style={styles.button}
              onPress={onSync}
              disabled={busy}>
              <Text style={styles.buttonText}>{t('AUTH_SYNC_NOW')}</Text>
            </UIPressable>
            */}
            <View style={styles.buttonGroup}>
              <UIPressable
                style={[styles.button, styles.buttonDanger, styles.buttonFullWidth]}
                onPress={onSignOut}
                disabled={busy}>
                <Text style={[styles.buttonText, styles.buttonDangerText]}>
                  {t('AUTH_SIGN_OUT')}
                </Text>
              </UIPressable>
            </View>
          </>
        ) : showForgotPassword ? (
          <>
            <Text style={styles.text}>{t('AUTH_FORGOT_PASSWORD_HINT')}</Text>
            <View style={styles.inputGroup}>
              <Text style={styles.label}>{t('AUTH_EMAIL')}</Text>
              <TextInput
                style={styles.input}
                value={email}
                onChangeText={setEmail}
                autoCapitalize="none"
                autoCorrect={false}
                keyboardType="email-address"
                placeholder="email@example.com"
                placeholderTextColor={theme.colors.tabIcon}
              />
            </View>
            <View style={styles.buttonGroup}>
              <UIPressable
                style={[styles.button, styles.buttonFullWidth]}
                onPress={onSendResetEmail}
                disabled={busy}>
                <Text style={styles.buttonText}>{t('AUTH_SEND_RESET_LINK')}</Text>
              </UIPressable>
            </View>
            <UIPressable
              style={styles.linkButton}
              onPress={() => {
                setShowForgotPassword(false);
                setError('');
                setMessage('');
              }}
              disabled={busy}>
              <Text style={styles.linkText}>{t('AUTH_BACK_TO_SIGN_IN')}</Text>
            </UIPressable>
          </>
        ) : (
          <>
            <View style={styles.inputGroup}>
              <Text style={[styles.label, styles.labelGroupFirst]}>
                {t('AUTH_EMAIL')}
              </Text>
              <TextInput
                style={styles.input}
                value={email}
                onChangeText={setEmail}
                autoCapitalize="none"
                autoCorrect={false}
                keyboardType="email-address"
                placeholder="email@example.com"
                placeholderTextColor={theme.colors.tabIcon}
              />
              <Text style={styles.label}>{t('AUTH_PASSWORD')}</Text>
              <TextInput
                style={styles.input}
                value={password}
                onChangeText={setPassword}
                secureTextEntry
                placeholder="••••••••"
                placeholderTextColor={theme.colors.tabIcon}
              />
            </View>
            <UIPressable
              style={styles.linkButton}
              onPress={() => {
                setShowForgotPassword(true);
                setError('');
                setMessage('');
              }}
              disabled={busy}>
              <Text style={styles.linkText}>{t('AUTH_FORGOT_PASSWORD')}</Text>
            </UIPressable>
            <View style={styles.buttonGroup}>
              {hasMissingSignInFields ? (
                <Text style={styles.validationText}>{t('AUTH_REQUIRED_FIELDS')}</Text>
              ) : null}
              <UIPressable
                style={[
                  styles.button,
                  styles.buttonFullWidth,
                  isSignInDisabled ? styles.buttonDisabled : null,
                ]}
                onPress={onSignIn}
                disabled={isSignInDisabled}>
                <Text
                  style={[
                    styles.buttonText,
                    isSignInDisabled ? styles.buttonTextDisabled : null,
                  ]}>
                  {t('AUTH_SIGN_IN')}
                </Text>
              </UIPressable>
              <UIPressable
                style={[
                  styles.button,
                  styles.buttonSecondary,
                  styles.buttonFullWidth,
                  styles.buttonStacked,
                  isSignInDisabled ? styles.buttonDisabled : null,
                ]}
                onPress={onSignUp}
                disabled={isSignInDisabled}>
                <Text
                  style={[
                    styles.buttonText,
                    styles.buttonSecondaryText,
                    isSignInDisabled ? styles.buttonTextDisabled : null,
                  ]}>
                  {t('AUTH_SIGN_UP')}
                </Text>
              </UIPressable>
            </View>
          </>
        )}
        {busy && (
          <ActivityIndicator
            style={styles.spinner}
            color={theme.colors.primary}
          />
        )}
        {error ? <Text style={styles.error}>{error}</Text> : null}
        {message ? <Text style={styles.success}>{message}</Text> : null}
      </View>
    </UIScreenScrollWrapper>
  );
};

function getStyles(theme) {
  return StyleSheet.create({
    box: {
      padding: theme.hMargin * 2,
      paddingBottom: 48,
      ...(IS_WEB
        ? {
            width: 440,
            maxWidth: '100%',
          }
        : {}),
    },
    centered: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
    },
    label: {
      fontFamily: theme.fontFamily,
      color: theme.colors.text,
      marginTop: 12,
      marginBottom: 6,
      fontSize: 14,
    },
    labelGroupFirst: {
      marginTop: 0,
    },
    inputGroup: {
      alignSelf: 'stretch',
      width: '100%',
    },
    buttonGroup: {
      alignSelf: 'stretch',
      width: '100%',
    },
    buttonFullWidth: {
      alignSelf: 'stretch',
      width: '100%',
    },
    buttonStacked: {
      marginTop: 12,
    },
    input: {
      fontFamily: theme.fontFamily,
      borderWidth: 1,
      borderColor: theme.colors.tabIcon,
      borderRadius: 8,
      padding: 12,
      color: theme.colors.text,
      backgroundColor: theme.colors.background,
      alignSelf: 'stretch',
      width: '100%',
    },
    text: {
      fontFamily: theme.fontFamily,
      color: theme.colors.text,
      fontSize: 16,
    },
    button: {
      backgroundColor: theme.colors.primary,
      padding: 14,
      borderRadius: 8,
      marginTop: 16,
      alignItems: 'center',
    },
    buttonDisabled: {
      backgroundColor: theme.colors.tabIcon,
    },
    buttonSecondary: {
      backgroundColor: '#1D3F8B',
    },
    buttonDanger: {
      backgroundColor: '#a94442',
    },
    buttonText: {
      fontFamily: theme.fontFamily,
      color: theme.colors.textInverted,
      fontSize: 16,
    },
    buttonTextDisabled: {
      color: theme.colors.background,
    },
    buttonDangerText: {
      color: '#fff',
    },
    buttonSecondaryText: {
      color: '#fff',
    },
    error: {
      marginTop: 16,
      color: '#c0392b',
      fontFamily: theme.fontFamily,
    },
    success: {
      marginTop: 16,
      color: theme.colors.primary,
      fontFamily: theme.fontFamily,
    },
    spinner: {
      marginTop: 16,
    },
    linkButton: {
      marginTop: 12,
      paddingVertical: 8,
      alignSelf: 'flex-start',
    },
    linkText: {
      fontFamily: theme.fontFamily,
      color: theme.colors.primary,
      fontSize: 15,
    },
    validationText: {
      marginTop: 10,
      color: '#c0392b',
      fontFamily: theme.fontFamily,
      fontSize: 13,
    },
  });
}

export default AuthScreen;
